import { Anchor, Badge, Box, Button, Card, CloseButton, Group, SegmentedControl, Skeleton, Stack, Text, TextInput, Title, VisuallyHidden } from '@mantine/core';
import { IconFilePlus, IconSearch } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { ClientRequestSummaryDto, ClientStage } from '@mexp/shared';
import { ErrorState } from '@/components/States';
import { useClientOptions, useClientRequests } from './api';
import { byUrgency, openFirst, productDetails, RepeatProductAction, RequestRow } from './RequestRow';
import {
  countriesOf,
  filtering,
  NO_FILTERS,
  passesFilters,
  readRequisitionFilters,
  RequisitionFilters,
  writeRequisitionFilters,
  type RequisitionFilterValues,
} from './RequisitionFilters';
import { dayMonth, stageLabel, STAGE_COLOR, STAGE_ORDER } from './stage';

type BoardStage = Exclude<ClientStage, 'cancelled'>;

/** The board of products by stage, or every requisition as a row. */
type Layout = 'product' | 'list';

interface ProductRow {
  key: string;
  name: string;
  total: number;
  /** The product's latest requisition: a new one for the same product starts from its details. */
  latest: ClientRequestSummaryDto;
  byStage: Record<BoardStage, ClientRequestSummaryDto[]>;
}

/** Product names as the client typed them: grouped regardless of case and spacing. */
const productKey = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

/** One row per product, each requisition under its current stage; cancelled ones stay in All dossiers. */
function productRows(items: ClientRequestSummaryDto[]): ProductRow[] {
  const rows = new Map<string, ProductRow>();
  for (const r of items) {
    if (r.stage === 'cancelled') continue;
    const key = productKey(r.productName);
    let row = rows.get(key);
    if (!row) {
      row = { key, name: r.productName.trim(), total: 0, latest: r, byStage: { received: [], in_preparation: [], final_review: [], completed: [] } };
      rows.set(key, row);
    }
    if (r.createdAt > row.latest.createdAt) row.latest = r;
    row.byStage[r.stage].push(r);
    row.total += 1;
  }
  for (const row of rows.values()) {
    for (const stage of STAGE_ORDER as BoardStage[]) {
      row.byStage[stage].sort(stage === 'completed' ? (a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? '') : byUrgency);
    }
  }
  return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
}

/** The second line of a requisition card: its code, then the date Makin gave or the version it reached. */
function meta(r: ClientRequestSummaryDto): string {
  if (r.stage === 'completed') return `${r.code}${r.releasedVersions > 1 ? ` · version ${r.releasedVersions}` : ''}`;
  const when = r.requiredBy ? `target completion ${dayMonth(r.requiredBy)}` : 'date to be confirmed';
  return `${r.code} · ${when}`;
}

function RequisitionCard({ r }: { r: ClientRequestSummaryDto }) {
  return (
    <Box
      component={Link}
      to={`/requests/${r.id}`}
      className="portal-board-item"
      aria-label={`${r.productName} for ${r.country}, ${r.code}, ${stageLabel(r.stage)}`}
    >
      <Text fz="sm" fw={600} lh={1.3} truncate="end">
        {r.country}
      </Text>
      <Text fz="xs" c="dimmed" lh={1.35} className="tabular">
        {meta(r)}
      </Text>
      {r.stage !== 'completed' && r.stage !== 'cancelled' && r.releasedVersions > 0 && (
        <Text fz="xs" fw={600} lh={1.35} c="var(--mantine-color-teal-light-color)">
          Dossier available · query open
        </Text>
      )}
      {r.openClarifications > 0 && (
        <Text fz="xs" fw={600} lh={1.35} c="var(--mantine-color-orange-light-color)">
          Makin needs your answer
        </Text>
      )}
    </Box>
  );
}

function ProductName({ row }: { row: ProductRow }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text fw={600} lh={1.3}>
          {row.name}
        </Text>
        {/* From the product's latest requisition. */}
        {productDetails(row.latest) && <Text fz="xs">{productDetails(row.latest)}</Text>}
        <Text fz="xs" c="dimmed" className="tabular">
          {row.total} requisition{row.total === 1 ? '' : 's'}
        </Text>
      </Stack>
      <RepeatProductAction from={row.latest.id} productName={row.name} />
    </Group>
  );
}

/** From lg: products down the side, stages across the top, each requisition in its stage's cell. */
function Board({ rows, stageCounts }: { rows: ProductRow[]; stageCounts: Record<BoardStage, number> }) {
  return (
    <Card p={0} visibleFrom="lg">
      <table className="portal-board">
        <VisuallyHidden component="caption">Requisitions by product and stage</VisuallyHidden>
        <thead>
          <tr>
            <th scope="col">
              <Text fz="xs" c="dimmed" fw={600}>
                Product
              </Text>
            </th>
            {(STAGE_ORDER as BoardStage[]).map((stage) => (
              <th key={stage} scope="col">
                <Group gap={6} wrap="nowrap">
                  <Badge color={STAGE_COLOR[stage]} variant="light" size="sm">
                    {stageLabel(stage)}
                  </Badge>
                  <Text fz="xs" c="dimmed" className="tabular">
                    {stageCounts[stage]}
                  </Text>
                </Group>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">
                <ProductName row={row} />
              </th>
              {(STAGE_ORDER as BoardStage[]).map((stage) => (
                <td key={stage}>
                  {row.byStage[stage].length ? (
                    <Stack gap={6}>
                      {row.byStage[stage].map((r) => (
                        <RequisitionCard key={r.id} r={r} />
                      ))}
                    </Stack>
                  ) : (
                    <Text fz="sm" c="dimmed" aria-label="None">
                      —
                    </Text>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** Every requisition as a row, open ones soonest expected first; each row can start another for its product. */
function RequisitionList({ items }: { items: ClientRequestSummaryDto[] }) {
  return (
    <Card p={6}>
      <Stack gap={2}>
        {items.map((r) => (
          <RequestRow key={r.id} r={r} />
        ))}
      </Stack>
    </Card>
  );
}

/** Below lg: the same board stacked, one product at a time, each requisition with its stage. */
function Stacked({ rows }: { rows: ProductRow[] }) {
  return (
    <Stack gap="md" hiddenFrom="lg">
      {rows.map((row) => (
        <Card key={row.key} p={{ base: 'md', sm: 'lg' }}>
          <Stack gap="sm">
            <ProductName row={row} />
            {(STAGE_ORDER as BoardStage[])
              .filter((stage) => row.byStage[stage].length)
              .map((stage) => (
                <Stack key={stage} gap={6}>
                  <Badge color={STAGE_COLOR[stage]} variant="light" size="sm" w="fit-content">
                    {stageLabel(stage)}
                  </Badge>
                  {row.byStage[stage].map((r) => (
                    <RequisitionCard key={r.id} r={r} />
                  ))}
                </Stack>
              ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

/** Cancelled requisitions stay in All dossiers, so this page filters by the other stages. */
const FILTER_STAGES = STAGE_ORDER as BoardStage[];

interface Filters extends RequisitionFilterValues {
  layout: Layout;
  q: string;
}

/**
 * Dossier requisition: every requisition on its own row; or, switched to By product, every product the company
 * has asked Makin about and where each of its requisitions stands. A product can have several requisitions
 * (other countries, later changes). New requisitions start here. Both can be narrowed by stage, country and
 * requisition type, and searched. The view, the filters and the search live in the URL, so the back button on a
 * dossier returns to them.
 */
export function ClientRequisitionsPage() {
  const options = useClientOptions();
  const list = useClientRequests('all');
  const [params, setParams] = useSearchParams();
  const current: Filters = {
    layout: params.get('view') === 'product' ? 'product' : 'list',
    q: params.get('q') ?? '',
    ...readRequisitionFilters(params, FILTER_STAGES),
  };
  const { layout, q } = current;
  const update = (next: Partial<Filters>) => {
    const f = { ...current, ...next };
    const out = new URLSearchParams();
    if (f.layout === 'product') out.set('view', 'product');
    if (f.q.trim()) out.set('q', f.q);
    writeRequisitionFilters(out, f);
    setParams(out, { replace: true });
  };
  const filters: RequisitionFilterValues = { stage: current.stage, country: current.country, type: current.type };
  const narrowed = filtering(filters);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const all = useMemo(() => productRows(items), [items]);
  const filtered = items.filter((r) => passesFilters(r, filters));
  const needle = q.trim().toLowerCase();
  const hit = (r: ClientRequestSummaryDto) => [r.productName, r.genericName, r.country, r.code].some((v) => v?.toLowerCase().includes(needle));
  const filteredRows = productRows(filtered);
  const rows = needle
    ? filteredRows.filter((row) => row.name.toLowerCase().includes(needle) || (STAGE_ORDER as BoardStage[]).some((s) => row.byStage[s].some(hit)))
    : filteredRows;
  const requisitionRows = openFirst(filtered.filter((r) => r.stage !== 'cancelled'));
  const listed = needle ? requisitionRows.filter(hit) : requisitionRows;
  const stageCounts = (STAGE_ORDER as BoardStage[]).reduce(
    (acc, stage) => ({ ...acc, [stage]: rows.reduce((n, row) => n + row.byStage[stage].length, 0) }),
    {} as Record<BoardStage, number>,
  );
  const requisitions = all.reduce((n, row) => n + row.total, 0);
  const cancelled = list.data?.counts.cancelled ?? 0;

  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Title order={1} fz={{ base: 26, sm: 32 }} style={{ letterSpacing: '-0.015em' }}>
            Dossier Requisition Form
          </Title>
          <Text c="dimmed">
            {options.data?.company.name ?? ' '}
            {list.data ? ` · ${all.length} product${all.length === 1 ? '' : 's'}, ${requisitions} requisition${requisitions === 1 ? '' : 's'}` : ''}
          </Text>
        </Stack>
        <Button component={Link} to="/requests/new" leftSection={<IconFilePlus size={16} />}>
          New requisition
        </Button>
      </Group>

      {list.isLoading ? (
        <Card>
          <Stack gap="sm">
            <Skeleton h={56} />
            <Skeleton h={56} />
            <Skeleton h={56} />
          </Stack>
        </Card>
      ) : !all.length ? (
        <Card p={{ base: 'lg', sm: 'xl' }}>
          <Stack gap="sm" align="flex-start" maw={560}>
            <Text fw={600}>No requisitions yet</Text>
            <Text fz="sm" c="dimmed">
              Send Makin a dossier requisition for a product and destination. Each product collects its requisitions here, grouped by stage.
            </Text>
            <Button component={Link} to="/requests/new" leftSection={<IconFilePlus size={16} />} mt="xs">
              New requisition
            </Button>
          </Stack>
        </Card>
      ) : (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <SegmentedControl
              value={layout}
              onChange={(v) => update({ layout: v as Layout })}
              data={[
                { value: 'list', label: 'Requisitions' },
                { value: 'product', label: 'By product' },
              ]}
            />
            <TextInput
              aria-label={layout === 'list' ? 'Find a requisition' : 'Find a product'}
              placeholder="Brand/generic name, country or code"
              leftSection={<IconSearch size={16} />}
              rightSection={q ? <CloseButton size="sm" aria-label="Clear the search" onClick={() => update({ q: '' })} /> : null}
              value={q}
              onChange={(e) => update({ q: e.currentTarget.value })}
              w={{ base: '100%', sm: 320 }}
            />
          </Group>
          <RequisitionFilters
            value={filters}
            onChange={(f) => update(f)}
            stages={FILTER_STAGES}
            countries={countriesOf(items, current.country)}
          />
          {layout === 'list' && listed.length ? (
            <RequisitionList items={listed} />
          ) : layout === 'product' && rows.length ? (
            <>
              <Board rows={rows} stageCounts={stageCounts} />
              <Stacked rows={rows} />
            </>
          ) : (
            <Card>
              <Stack gap="xs" align="flex-start">
                <Text fz="sm">
                  No {layout === 'list' ? 'requisition' : 'product'} matches {needle ? `“${q.trim()}”` : 'these filters'}
                  {needle && narrowed ? ' with these filters' : ''}.
                </Text>
                <Button size="compact-sm" variant="subtle" onClick={() => update({ q: '', ...NO_FILTERS })}>
                  Clear the search and filters
                </Button>
              </Stack>
            </Card>
          )}
          {cancelled > 0 && (
            <Text fz="xs" c="dimmed">
              {cancelled} cancelled requisition{cancelled === 1 ? ' is' : 's are'} kept in{' '}
              <Anchor component={Link} to="/requests?view=cancelled" fz="xs">
                All dossiers
              </Anchor>
              .
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
